import React, { FunctionComponent } from "react";
import { MenuPlacement } from "react-select";
import { Column, SortingRule, TableInstance, TableState } from "react-table";
import { OptionsProps, PaginationComponent } from "../Pagination";
import { Table as CustomTable } from "./CustomTable";
import { Styles } from "./styles";

interface Props {
    columns: Column[];
    changePageSize?: (arg0: number) => void;
    data: object[];
    isFetchingData?: boolean;
    getInstanceCallback?: (arg0: TableInstance) => void;
    handlePageChange?: (selectedItem: { selected: number }) => void;
    showCheckBoxColumn?: boolean;
    initialPageIndex?: number;
    pageSize?: number;
    pageNumber: number;
    showRowsPerPageInfo?: boolean;
    totalCount: number;
    selectedCount?: number;
    menuPlacement?: MenuPlacement;
    hideRowsPerPageBar?: boolean;
    hidePageLengthChangeDropdown?: boolean;
    pageLengthChangeDropdownOptions?: Array<OptionsProps>;
    onSort?: (params: { sortBy: SortingRule<{}>[]; pageSize: number | undefined }) => void;
    filterPosition?: (id: string) => void;
    useControlledState?: (state: TableState) => void;
    onRowSelect?: (param1: Record<string, boolean>, param2: TableInstance) => void;
    hideSelectedRowsText?: boolean;
    customSingleCheckboxLogic?: (
        row: {
            isSelected: boolean;
            id: number;
            values: {
                regId?: number;
                regulationId?: number;
                id?: number;
            };
            original: {
                regId?: number;
                curb_link?: {
                    id: number;
                };
                regulationId?: number;
                id?: number;
            };
            toggleRowSelected: (flag?: boolean) => void;
        },
        props: TableInstance
    ) => void;
    customSelectAllCheckboxLogic?: (props: TableInstance) => void;
    resetCondition?: boolean;
    regulationListViewState?: Record<string, number[]>;
    selectPreviouslySelectedRows?: (instance: TableInstance) => void;
    storeSelectedRowsDataInStore?: (
        param1: number[],
        param2: number[],
        param3: number[],
        param4: number[],
        param5: string[],
        param6: number[],
        instance: TableInstance
    ) => void;
}

export const Table: FunctionComponent<Props> = ({
    changePageSize,
    getInstanceCallback,
    handlePageChange,
    onSort,
    columns,
    data,
    isFetchingData,
    hidePageLengthChangeDropdown,
    hideRowsPerPageBar,
    initialPageIndex,
    menuPlacement,
    pageLengthChangeDropdownOptions,
    pageSize,
    pageNumber,
    showCheckBoxColumn,
    showRowsPerPageInfo,
    totalCount,
    selectedCount,
    filterPosition,
    useControlledState,
    onRowSelect,
    hideSelectedRowsText,
    customSingleCheckboxLogic,
    customSelectAllCheckboxLogic,
    resetCondition,
    regulationListViewState,
    selectPreviouslySelectedRows,
    storeSelectedRowsDataInStore
}): JSX.Element => {
    const memoizedData = React.useMemo(() => data, [data]);
    const memoizedColumns = React.useMemo(() => columns, [columns]);
    const hasFilters = columns.some((column) => Boolean(column.Filter));

    return (
        <Styles>
            <CustomTable
                columns={memoizedColumns}
                customSelectAllCheckboxLogic={customSelectAllCheckboxLogic}
                customSingleCheckboxLogic={customSingleCheckboxLogic}
                data={memoizedData}
                filterPosition={filterPosition}
                getInstanceCallback={getInstanceCallback}
                isFetchingData={isFetchingData}
                name="CustomTable"
                pageSize={pageSize}
                regulationListViewState={regulationListViewState}
                resetCondition={resetCondition}
                rowCount={memoizedData.length}
                selectPreviouslySelectedRows={selectPreviouslySelectedRows}
                storeSelectedRowsDataInStore={storeSelectedRowsDataInStore}
                useControlledState={useControlledState}
                onRowSelect={onRowSelect}
                onSort={onSort}
                {...{
                    handlePageChange,
                    hasFilters,
                    initialPageIndex,
                    showCheckBoxColumn,
                    showRowsPerPageInfo
                }}
            />
            {!isFetchingData && memoizedData.length > 0 && (
                <PaginationComponent
                    handlePagination={handlePageChange}
                    hideSelectedRowsText={hideSelectedRowsText}
                    pageNumber={pageNumber}
                    rowsPerPage={pageSize}
                    selectedCount={selectedCount}
                    totalCount={totalCount}
                    {...{
                        changePageSize,
                        hidePageLengthChangeDropdown,
                        hideRowsPerPageBar,
                        initialPageIndex,
                        menuPlacement,
                        pageLengthChangeDropdownOptions
                    }}
                />
            )}
        </Styles>
    );
};
Table.defaultProps = {
    getInstanceCallback: (): null => null,
    hideSelectedRowsText: true,
    initialPageIndex: 0,
    isFetchingData: false,
    pageLengthChangeDropdownOptions: [
        {
            label: 10,
            value: 10
        },
        {
            label: 15,
            value: 15
        },
        {
            label: 20,
            value: 20
        }
    ],
    pageNumber: 0,
    pageSize: 10,
    selectedCount: 0,
    showCheckBoxColumn: false,
    showRowsPerPageInfo: true
};
